import type { ChangeEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { apiFetch, ApiError, NetworkError } from '@/lib/api-client';
import type { Child } from '@/lib/auth-types';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';

interface ChildSelectorProps {
  value: string | null;
  onChange: (childId: string) => void;
  label?: string;
  labelClassName?: string;
  id?: string;
}

export function ChildSelector({ value, onChange, label, labelClassName, id }: ChildSelectorProps) {
  const [children, setChildren] = useState<Child[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Stable ref so the fetch effect never needs to re-run when the parent
  // passes a fresh onChange identity on every render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    let cancelled = false;
    apiFetch<Child[]>('/api/children')
      .then((list) => {
        if (cancelled) return;
        setChildren(list);
        if (list.length === 1 && list[0]) {
          onChangeRef.current(list[0].id);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof NetworkError) {
          setError('Keine Verbindung. Bitte prüfe deine Internetverbindung.');
        } else if (err instanceof ApiError) {
          setError(err.envelope?.user_message ?? STRINGS.builder.shared.childSelectorErrorLoad);
        } else {
          setError(STRINGS.builder.shared.childSelectorErrorLoad);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const labelNode: ReactNode = label ? (
    <Label htmlFor={id} className={cn('text-sm text-dream-navy', labelClassName)}>
      {label}
    </Label>
  ) : null;

  if (error !== null) {
    return (
      <div className="space-y-1.5">
        {labelNode}
        <p className="text-xs text-[color:var(--color-error)]">{error}</p>
      </div>
    );
  }

  if (children === null) {
    return (
      <div className="space-y-1.5">
        {labelNode}
        <div className="flex h-11 items-center gap-2 text-sm text-dream-navy/70">
          <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          Wird geladen…
        </div>
      </div>
    );
  }

  // Single child → auto-select handled above, hide the entire block (label + select)
  if (children.length === 1) {
    return null;
  }

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  return (
    <div className="space-y-1.5">
      {labelNode}
      <div className="relative">
        <select
          id={id}
          value={value ?? ''}
          onChange={handleChange}
          className="builder-field h-11 w-full appearance-none px-3 pr-10 text-base text-dream-navy outline-none"
        >
          <option value="" disabled>
            {STRINGS.builder.shared.childSelectorPlaceholder}
          </option>
          {children.map((child) => (
            <option key={child.id} value={child.id}>
              {child.name}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-dream-navy"
        />
      </div>
    </div>
  );
}
